import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import ChipsInput from "@/components/ChipsInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES, EMPLOYMENT_TYPES, REMOTE_SCOPES, TRAVEL_LEVELS } from "@/lib/constants";
import { titleCase } from "@/lib/format";
import { ArrowRight, ArrowLeft, Check, FilePlus, Sparkle, Lock, Scales, Info } from "@phosphor-icons/react";

const STEPS = ["Location & eligibility", "Remote & work model", "Salary & employment", "Non-negotiables", "Review"];

export default function CareerFit() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [a, setA] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => {
      const p = r.data;
      const id = p.identity || {}, pref = p.preferences || {}, con = p.constraints || {};
      setA({
        country_of_residence: id.country_of_residence || "",
        legal_work_countries: id.legal_work_countries || [],
        relocation_willing: id.relocation_willing ?? null,
        remote_scope_pref: pref.remote_scope_pref || "",
        no_regular_office: !!con.no_regular_office,
        max_office_days_per_week: pref.max_office_days_per_week ?? null,
        travel_tolerance: pref.travel_tolerance || "",
        min_salary: pref.min_salary ?? null,
        salary_currency: pref.salary_currency || "",
        employment_types: pref.employment_types || [],
        no_relocation: !!con.no_relocation,
        no_onsite_only: !!con.no_onsite_only,
        no_mandatory_video: !!con.no_mandatory_video,
        hard: con.hard || [],
        strong: con.strong || [],
        flexible: con.flexible || [],
      });
    });
  }, []);

  const set = (patch) => setA((prev) => ({ ...prev, ...patch }));
  const toggleType = (t) =>
    set({ employment_types: a.employment_types.includes(t) ? a.employment_types.filter((x) => x !== t) : [...a.employment_types, t] });

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        identity: {
          country_of_residence: a.country_of_residence || null,
          legal_work_countries: a.legal_work_countries,
          relocation_willing: a.relocation_willing,
        },
        preferences: {
          remote_scope_pref: a.remote_scope_pref || null,
          max_office_days_per_week: a.max_office_days_per_week,
          travel_tolerance: a.travel_tolerance || null,
          min_salary: a.min_salary,
          salary_currency: a.salary_currency || null,
          employment_types: a.employment_types,
        },
        constraints: {
          no_regular_office: a.no_regular_office,
          no_relocation: a.no_relocation,
          no_onsite_only: a.no_onsite_only,
          no_mandatory_video: a.no_mandatory_video,
          hard: a.hard, strong: a.strong, flexible: a.flexible,
        },
        completed: true,
      });
      toast.success("Career fit assessment saved");
      navigate("/matches");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!a) return <p className="text-slate-400 text-sm">Loading…</p>;

  return (
    <div>
      <PageHeader
        title="Career fit assessment"
        subtitle="A guided set of questions that becomes the source of truth for your preferences and constraints. Answer only what applies — you can skip anything, and unknown stays unknown."
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
        {/* Guided assessment */}
        <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="career-fit-wizard">
          <Stepper step={step} />

          {step === 0 && (
            <StepBody title="Where can you legally work?" note="These are hard eligibility facts — never guessed from your CV.">
              <Field label="Country of residence">
                <Select value={a.country_of_residence} onValueChange={(v) => set({ country_of_residence: v })}>
                  <SelectTrigger data-testid="cf-residence"><SelectValue placeholder="Select (optional)" /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Countries where you may legally work">
                <ChipsInput value={a.legal_work_countries} onChange={(v) => set({ legal_work_countries: v })} placeholder="Add a country" testid="cf-legal-countries" />
              </Field>
              <Field label="Are you willing to relocate?">
                <Select value={a.relocation_willing === true ? "yes" : a.relocation_willing === false ? "no" : "unspecified"}
                  onValueChange={(v) => set({ relocation_willing: v === "yes" ? true : v === "no" ? false : null })}>
                  <SelectTrigger data-testid="cf-relocation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unspecified">Prefer not to say / unknown</SelectItem>
                    <SelectItem value="yes">Yes, open to relocating</SelectItem>
                    <SelectItem value="no">No — this is a hard limit</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </StepBody>
          )}

          {step === 1 && (
            <StepBody title="How do you want to work?" note="Flexible ranges let a role pass inside your stated boundary (shown as 'Flexibility used'). A non-negotiable excludes a role outright.">
              <Field label="Preferred remote scope">
                <Select value={a.remote_scope_pref} onValueChange={(v) => set({ remote_scope_pref: v })}>
                  <SelectTrigger data-testid="cf-remote-scope"><SelectValue placeholder="Select (optional)" /></SelectTrigger>
                  <SelectContent>{REMOTE_SCOPES.map((s) => <SelectItem key={s} value={s}>{titleCase(s.replace(/_/g, " "))}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <HardToggle label="No regular (weekly) office attendance" checked={a.no_regular_office} onChange={(v) => set({ no_regular_office: v })} testid="cf-no-office" />
              <Field label="Max office days per week you'd accept (flexible)">
                <Input type="number" value={a.max_office_days_per_week ?? ""} onChange={(e) => set({ max_office_days_per_week: e.target.value === "" ? null : Number(e.target.value) })} placeholder="e.g. 2" data-testid="cf-office-days" />
              </Field>
              <Field label="Travel tolerance (flexible)">
                <Select value={a.travel_tolerance} onValueChange={(v) => set({ travel_tolerance: v })}>
                  <SelectTrigger data-testid="cf-travel"><SelectValue placeholder="Select (optional)" /></SelectTrigger>
                  <SelectContent>{TRAVEL_LEVELS.map((t) => <SelectItem key={t} value={t}>{titleCase(t)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </StepBody>
          )}

          {step === 2 && (
            <StepBody title="Pay & employment type" note="A minimum salary is treated as a hard floor only when a role states a comparable salary; unknown salaries stay unknown.">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Minimum salary (optional)">
                  <Input type="number" value={a.min_salary ?? ""} onChange={(e) => set({ min_salary: e.target.value === "" ? null : Number(e.target.value) })} placeholder="e.g. 50000" data-testid="cf-min-salary" />
                </Field>
                <Field label="Currency">
                  <Input value={a.salary_currency} onChange={(e) => set({ salary_currency: e.target.value })} placeholder="e.g. EUR" data-testid="cf-currency" />
                </Field>
              </div>
              <Field label="Employment types you'd accept">
                <div className="flex flex-wrap gap-2">
                  {EMPLOYMENT_TYPES.map((t) => (
                    <button key={t} type="button" onClick={() => toggleType(t)} data-testid={`cf-emp-${t}`}
                      className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${a.employment_types.includes(t) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                      {titleCase(t)}
                    </button>
                  ))}
                </div>
              </Field>
            </StepBody>
          )}

          {step === 3 && (
            <StepBody title="Your non-negotiables" note="A role breaking a non-negotiable is excluded. Strong preferences affect ranking; flexible items are where you'll bend.">
              <div className="rounded-md border border-red-200 bg-red-50 p-4 space-y-2">
                <p className="font-heading font-bold text-red-900 text-sm">Non-negotiable (hard)</p>
                <HardToggle label="No relocation" checked={a.no_relocation} onChange={(v) => set({ no_relocation: v })} testid="cf-no-reloc" light />
                <HardToggle label="No onsite-only roles" checked={a.no_onsite_only} onChange={(v) => set({ no_onsite_only: v })} testid="cf-no-onsite" light />
                <HardToggle label="No mandatory video application" checked={a.no_mandatory_video} onChange={(v) => set({ no_mandatory_video: v })} testid="cf-no-video" light />
                <div className="pt-1"><ChipsInput value={a.hard} onChange={(v) => set({ hard: v })} placeholder="Add another hard limit" testid="cf-hard" /></div>
              </div>
              <Field label="Strong preferences (affect ranking, not a rejection)">
                <ChipsInput value={a.strong} onChange={(v) => set({ strong: v })} placeholder="Add a strong preference" testid="cf-strong" />
              </Field>
              <Field label="Flexible — where you're willing to bend">
                <ChipsInput value={a.flexible} onChange={(v) => set({ flexible: v })} placeholder="Add a flexible item" testid="cf-flexible" />
              </Field>
            </StepBody>
          )}

          {step === 4 && (
            <StepBody title="Review & save" note="These answers define your preferences and constraints. Your factual background (jobs, education, skills) is imported separately from your CV on the right.">
              <ul className="text-sm text-slate-700 space-y-1.5" data-testid="cf-review">
                <ReviewLine label="Residence" value={a.country_of_residence} />
                <ReviewLine label="Legal work countries" value={a.legal_work_countries.join(", ")} />
                <ReviewLine label="Relocation" value={a.relocation_willing === true ? "Willing" : a.relocation_willing === false ? "Not willing (hard)" : "Unknown"} />
                <ReviewLine label="Remote scope" value={a.remote_scope_pref && titleCase(a.remote_scope_pref.replace(/_/g, " "))} />
                <ReviewLine label="Non-negotiables" value={[a.no_regular_office && "No weekly office", a.no_relocation && "No relocation", a.no_onsite_only && "No onsite-only", a.no_mandatory_video && "No video", ...a.hard].filter(Boolean).join(", ")} />
                <ReviewLine label="Min salary" value={a.min_salary ? `${a.min_salary} ${a.salary_currency || ""}` : ""} />
                <ReviewLine label="Employment types" value={a.employment_types.map(titleCase).join(", ")} />
              </ul>
            </StepBody>
          )}

          <div className="mt-6 flex items-center justify-between">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="cf-back" className="gap-1.5 text-slate-500"><ArrowLeft /> Back</Button>
            <div className="flex items-center gap-2">
              {step < STEPS.length - 1 && (
                <Button variant="outline" onClick={() => setStep((s) => s + 1)} data-testid="cf-skip" className="border-slate-300 text-slate-500">Skip</Button>
              )}
              {step < STEPS.length - 1 ? (
                <Button onClick={() => setStep((s) => s + 1)} data-testid="cf-next" className="bg-slate-900 hover:bg-slate-800 gap-1.5">Next <ArrowRight weight="bold" /></Button>
              ) : (
                <Button onClick={finish} disabled={saving} data-testid="cf-finish" className="bg-slate-900 hover:bg-slate-800 gap-1.5"><Check weight="bold" /> {saving ? "Saving…" : "Save assessment"}</Button>
              )}
            </div>
          </div>
        </div>

        {/* CV import — review before confirm */}
        <CvImport />
      </div>
    </div>
  );
}

function CvImport() {
  const fileRef = useRef(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [extraction, setExtraction] = useState(null); // {extraction_id, facts}
  const [inc, setInc] = useState({}); // include flags keyed by "type-index"

  const LIST_KEYS = ["skills", "tools", "certifications", "industries", "role_types"];
  const REC_KEYS = ["work_history", "education", "languages"];

  const extract = async (file) => {
    setLoading(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      else fd.append("text", text);
      const { data } = await api.post("/profile/cv-extract", fd);
      setExtraction(data);
      const flags = {};
      [...LIST_KEYS, ...REC_KEYS].forEach((k) => (data.facts[k] || []).forEach((_, i) => (flags[`${k}-${i}`] = true)));
      setInc(flags);
      toast.success("CV read — review the facts before adding them");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const f = extraction.facts;
      const facts = { headline: f.headline || null };
      LIST_KEYS.forEach((k) => (facts[k] = (f[k] || []).filter((_, i) => inc[`${k}-${i}`])));
      REC_KEYS.forEach((k) => (facts[k] = (f[k] || []).filter((_, i) => inc[`${k}-${i}`])));
      await api.post("/profile/cv-apply", { facts, extraction_id: extraction.extraction_id });
      toast.success("Confirmed facts added to your profile");
      setExtraction(null); setText("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setApplying(false); }
  };

  const recLabel = (k, item) => {
    if (k === "languages") return `${item.language}${item.level ? ` · ${item.level}` : ""}`;
    if (k === "education") return [item.degree, item.field, item.institution, item.year].filter(Boolean).join(" · ") || "Education entry";
    return [item.title, item.employer, [item.start, item.end].filter(Boolean).join("–")].filter(Boolean).join(" · ") || "Role";
  };

  return (
    <div className="lg:sticky lg:top-6 space-y-4" data-testid="cv-import">
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-5">
        <div className="flex items-center gap-2 text-indigo-900"><Sparkle weight="fill" className="text-indigo-500" /><p className="font-semibold text-sm">Import facts from your CV</p></div>
        <p className="mt-1 text-xs text-indigo-800/80 leading-relaxed">
          We extract factual background only (jobs, education, skills, languages). We never read preferences from a CV, and <strong>nothing is saved until you confirm</strong>.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="cv-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) extract(f); e.target.value = ""; }} />
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={loading} data-testid="cv-upload-btn" className="bg-slate-900 hover:bg-slate-800 gap-1.5"><FilePlus weight="fill" /> {loading ? "Reading…" : "Upload CV (PDF/DOCX)"}</Button>
        </div>
        <div className="mt-3">
          <Label className="text-xs text-indigo-800">…or paste your CV text</Label>
          <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste your CV here" data-testid="cv-text-input" className="mt-1 bg-white" />
          <Button size="sm" variant="outline" disabled={loading || text.trim().length < 30} onClick={() => extract(null)} data-testid="cv-extract-text-btn" className="mt-2 border-indigo-300 text-indigo-700">Extract from text</Button>
        </div>
      </div>

      {extraction && (
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="cv-review">
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 mb-3">
            <Info weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800 leading-relaxed"><span className="font-semibold">From your CV — review before saving.</span> Untick anything that's wrong or that you don't want stored. Ticked items are added to your profile; nothing is overwritten.</p>
          </div>

          {[...REC_KEYS, ...LIST_KEYS].map((k) => {
            const items = extraction.facts[k] || [];
            if (!items.length) return null;
            return (
              <div key={k} className="mb-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-1.5">{titleCase(k.replace(/_/g, " "))} <span className="normal-case text-slate-400">· from CV</span></p>
                <div className="space-y-1.5">
                  {items.map((item, i) => {
                    const key = `${k}-${i}`;
                    const text = REC_KEYS.includes(k) ? recLabel(k, item) : String(item);
                    return (
                      <label key={key} className="flex items-start gap-2 cursor-pointer" data-testid={`cv-item-${key}`}>
                        <Checkbox checked={!!inc[key]} onCheckedChange={(v) => setInc((s) => ({ ...s, [key]: !!v }))} className="mt-0.5" />
                        <span className={`text-sm ${inc[key] ? "text-slate-800" : "text-slate-400 line-through"}`}>{text}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <Button onClick={apply} disabled={applying} data-testid="cv-apply-btn" className="w-full bg-slate-900 hover:bg-slate-800 gap-1.5 mt-1"><Check weight="bold" /> {applying ? "Adding…" : "Add confirmed facts to profile"}</Button>
        </div>
      )}

      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 flex items-start gap-2">
        <Scales weight="fill" className="text-slate-400 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-600 leading-relaxed">Preferences and constraints come only from the guided assessment. Facts come from your CV after you confirm them. The two are kept strictly separate.</p>
      </div>
    </div>
  );
}

/* ---- helpers ---- */
function Stepper({ step }) {
  return (
    <div className="flex items-center gap-1.5 mb-6" data-testid="cf-stepper">
      {STEPS.map((label, i) => (
        <div key={label} className="flex-1">
          <div className={`h-1.5 rounded-full ${i <= step ? "bg-slate-900" : "bg-slate-200"}`} />
          <p className={`mt-1.5 text-[11px] ${i === step ? "text-slate-900 font-semibold" : "text-slate-400"} hidden sm:block`}>{label}</p>
        </div>
      ))}
    </div>
  );
}
function StepBody({ title, note, children }) {
  return (
    <div>
      <h2 className="font-heading font-bold text-lg text-slate-900">{title}</h2>
      {note && <p className="text-sm text-slate-500 mt-1 mb-4">{note}</p>}
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (<div><Label className="mb-1.5 block">{label}</Label>{children}</div>);
}
function HardToggle({ label, checked, onChange, testid, light }) {
  return (
    <label className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2.5 cursor-pointer ${light ? "border-red-200 bg-white" : "border-slate-200"}`}>
      <span className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><Lock size={13} className="text-slate-400" /> {label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}
function ReviewLine({ label, value }) {
  return (
    <li className="flex justify-between gap-4 border-b border-slate-100 pb-1.5">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 text-right">{value || <span className="text-slate-400">Unknown</span>}</span>
    </li>
  );
}
